import React, { useState } from 'react';
import { Upload, DollarSign, Calendar, CheckCircle2, X } from 'lucide-react';
import toast, { Toaster } from 'react-hot-toast';

const AddCampaign = () => {
  const [formData, setFormData] = useState({
    title: '', category: 'Education', goal: '', startDate: '', endDate: '', shortDesc: '', fullDesc: ''
  });
  const [images, setImages] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCustomCategory, setIsCustomCategory] = useState(false);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const filesArray = Array.from(e.target.files);
      setImages(prev => [...prev, ...filesArray]);
      const newPreviews = filesArray.map(file => URL.createObjectURL(file));
      setPreviews(prev => [...prev, ...newPreviews]);
    }
  };

  const removeImage = (index: number) => {
    setImages(images.filter((_, i) => i !== index));
    setPreviews(previews.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (images.length === 0) {
      toast.error('Please upload at least one image!');
      return;
    }

    setIsLoading(true);
    const loadingToast = toast.loading('Publishing Campaign...');

    const formDataToSend = new FormData();
    formDataToSend.append('title', formData.title);
    formDataToSend.append('category', formData.category);
    formDataToSend.append('goal_amount', formData.goal);
    formDataToSend.append('short_description', formData.shortDesc);
    formDataToSend.append('full_description', formData.fullDesc);
    formDataToSend.append('start_date', formData.startDate);
    formDataToSend.append('end_date', formData.endDate);
    
    // Multiple images attach karna
    images.forEach((img) => formDataToSend.append('images[]', img));

    try {
      const token = localStorage.getItem("sevaa_admin_token");
      // const response = await fetch("http://localhost:1415/api/campaigns/create", {
      // const response = await fetch("http://localhost/seva-backend/api/campaigns/create.php", {
      const response = await fetch("https://www.sevaafoundation.com/seva-backend/api/campaigns/create.php", {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}` },
        body: formDataToSend,
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Campaign Published Successfully!', { id: loadingToast });
        // Form Reset
        setFormData({ title: '', category: 'Education', goal: '', startDate: '', endDate: '', shortDesc: '', fullDesc: '' });
        setImages([]); setPreviews([]);
      } else {
        toast.error(data.error || 'Failed to publish', { id: loadingToast });
      }
    } catch (err) {
      toast.error('Server connection failed!', { id: loadingToast });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6 animate-fade-in-up pb-10">
      <Toaster position="top-right" />
      <div>
        <h1 className="text-3xl font-bold text-slate-800">Create New Campaign</h1>
        <p className="text-slate-500 mt-1">Launch a new fundraising initiative.</p>
      </div>

      <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Form Fields */}
        <div className="lg:col-span-2 space-y-6 bg-white p-6 sm:p-8 rounded-2xl shadow-sm border border-slate-100">
          <div>
            <label className="block text-sm font-bold text-slate-700 mb-2 uppercase">Campaign Title</label>
            <input type="text" required value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-orange-500 outline-none" placeholder="e.g. Save Earth" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
           <div>
              <label className="block text-sm font-bold text-slate-700 mb-2 uppercase flex justify-between">
                Category
                {/* Agar custom mode me hain, toh wapas dropdown pe jaane ka button */}
                {isCustomCategory && (
                  <button type="button" onClick={() => { setIsCustomCategory(false); setFormData({...formData, category: 'Education'}) }} className="text-orange-500 hover:text-orange-600 text-xs normal-case">Return to List</button>
                )}
              </label>
              
              {isCustomCategory ? (
                <input 
                  type="text" 
                  required 
                  autoFocus
                  value={formData.category}
                  onChange={(e) => setFormData({...formData, category: e.target.value})}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-orange-500 outline-none" 
                  placeholder="e.g. Animal Rescue, Winter Drive..." 
                />
              ) : (
                <select 
                  value={formData.category} 
                  onChange={(e) => {
                    if (e.target.value === 'Other') {
                      setIsCustomCategory(true);
                      setFormData({...formData, category: ''}); // input field ko khaali karne ke liye
                    } else {
                      setFormData({...formData, category: e.target.value});
                    }
                  }} 
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-orange-500 outline-none"
                >
                  <option>Education</option>
                  <option>Environment</option>
                  <option>Medical</option>
                  <option>Livelihood</option>
                  <option>Disaster Response</option>
                  <option>Women Empowerment</option>
                  <option value="Other" className="font-bold text-orange-600">Other (Type Custom...)</option>
                </select>
              )}
            </div>
            <div>
              <label className="block text-sm font-bold text-slate-700 mb-2 uppercase">Goal Amount (₹)</label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                <input type="number" required value={formData.goal} onChange={(e) => setFormData({...formData, goal: e.target.value})} className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-orange-500" placeholder="50000" />
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-bold text-slate-700 mb-2 uppercase">Short Description</label>
            <textarea rows={2} required value={formData.shortDesc} onChange={(e) => setFormData({...formData, shortDesc: e.target.value})} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-orange-500" placeholder="Briefly explain..." />
          </div>

          <div>
            <label className="block text-sm font-bold text-slate-700 mb-2 uppercase">Full Details</label>
            <textarea rows={6} required value={formData.fullDesc} onChange={(e) => setFormData({...formData, fullDesc: e.target.value})} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-orange-500" placeholder="Detailed story..." />
          </div>
        </div>

        {/* Media & Action */}
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
            <label className="block text-sm font-bold text-slate-700 mb-4 uppercase">Campaign Images</label>
            <div className="relative border-2 border-dashed border-slate-200 bg-slate-50 hover:border-orange-400 rounded-2xl p-4 flex flex-col items-center justify-center min-h-[150px] cursor-pointer">
              <Upload className="text-slate-400 mb-2" size={32} />
              <p className="text-xs text-slate-500">Click to upload multiple images</p>
              <input type="file" multiple accept="image/*" onChange={handleImageChange} className="absolute inset-0 opacity-0 cursor-pointer" />
            </div>
            
            {previews.length > 0 && (
              <div className="mt-4 flex gap-2 flex-wrap">
                {previews.map((src, i) => (
                  <div key={i} className="relative w-16 h-16 rounded-lg overflow-hidden border">
                    <img src={src} className="w-full h-full object-cover" alt="Preview" />
                    <button type="button" onClick={() => removeImage(i)} className="absolute top-0 right-0 bg-red-500 text-white rounded-bl-lg p-0.5"><X size={12}/></button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 space-y-4">
            <h3 className="text-sm font-bold text-slate-700 uppercase flex items-center gap-2"><Calendar size={18} className="text-orange-500" /> Duration</h3>
            <input type="date" required value={formData.startDate} onChange={(e) => setFormData({...formData, startDate: e.target.value})} className="w-full px-3 py-2 bg-slate-50 border rounded-lg text-sm mb-2" />
            <input type="date" required value={formData.endDate} onChange={(e) => setFormData({...formData, endDate: e.target.value})} className="w-full px-3 py-2 bg-slate-50 border rounded-lg text-sm" />
          </div>

          <button type="submit" disabled={isLoading} className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 rounded-xl shadow-lg transition-all flex items-center justify-center gap-2 disabled:opacity-70">
            <CheckCircle2 size={20} /> {isLoading ? 'Publishing...' : 'Publish Campaign'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddCampaign;